import React from 'react';
import { Box, Button, ButtonBase, Chip, Paper, Tooltip } from '@mui/material';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import dataunfoldLogo from '../../images/logo-7-80x80.png';
import DataUnfoldOperations from './DataUnfoldOperations';
import ActionTrackingBubble from './ActionTrackingBubble';
import { PROJECT_WORKSPACE_NODE_HEIGHT, PROJECT_WORKSPACE_NODE_WIDTH } from './projectDagNodeDimensions';

export default function ProjectWorkspaceDagNode({ label, tagCount, receivedCount, active, reanalysis, actionControls, t }) {
  const canEditTags = Boolean(actionControls?.canManage && actionControls?.onEditTags);
  const hasTagCount = Number.isInteger(tagCount) && tagCount >= 0;
  const openDashboard = actionControls?.onOpenDashboard;
  const openTimeline = actionControls?.onOpenTimeline;
  const stopPropagation = (event) => event.stopPropagation();
  const activate = (callback) => (event) => { event.stopPropagation(); callback?.(); };
  const tagsLabel = t('dag.extraction_information', 'Information to extract');

  return <Paper data-testid="dag-dataunfold-core" elevation={0} sx={{
    position: 'relative', width: PROJECT_WORKSPACE_NODE_WIDTH, height: PROJECT_WORKSPACE_NODE_HEIGHT,
    boxSizing: 'border-box', p: 1.5, borderRadius: 2, border: '1px solid',
    borderColor: active ? 'primary.main' : 'divider', bgcolor: 'background.paper',
    boxShadow: active ? '0 0 0 3px rgba(87,87,176,.12), 0 6px 20px rgba(65,65,140,.12)'
      : '0 6px 20px rgba(65,65,140,.09)',
  }}>
    <ActionTrackingBubble label={t('dag.funnel_received_items', 'Items received')}
      count={Number.isInteger(receivedCount) && receivedCount >= 0 ? receivedCount : null}
      showCount testId="dag-workspace-tracking" t={t}
      onOpenEvents={actionControls?.onOpenTrackingEvents} />
    <ButtonBase className="nodrag nopan" disabled={!openDashboard}
      onPointerDown={stopPropagation} onKeyDown={stopPropagation} onClick={activate(openDashboard)}
      aria-label={t('dag.open_project_dashboard', 'Open dashboard for {{name}}', { name: label })}
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.75, px: 2.5,
        borderRadius: 1, '&.Mui-disabled': { color: 'text.primary' } }}>
      <Box component="img" src={dataunfoldLogo} alt="DataUnfold" draggable={false}
        sx={{ width: 36, height: 36, objectFit: 'contain' }} />
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'primary.main', fontSize: 10 }}>
        {t('dag.open_dashboard', 'Open dashboard')}<ArrowOutwardRoundedIcon sx={{ fontSize: 12 }} />
      </Box>
    </ButtonBase>
    <Tooltip title={canEditTags
      ? t('dag.extraction_information_hint', 'Choose which information to find in your data.')
      : t('dag.extraction_information_readonly', 'Only project managers can change the information to extract.')}>
      <Box component="span" className="nodrag nopan" onClick={stopPropagation} onPointerDown={stopPropagation}
        sx={{ display: 'block', mt: 0.5 }}>
        <Button data-testid="dag-tag-count" className="nodrag nopan" fullWidth size="small"
          disabled={!canEditTags} onClick={activate(actionControls?.onEditTags)}
          onPointerDown={stopPropagation} onKeyDown={stopPropagation}
          aria-label={hasTagCount
            ? t('dag.extraction_information_count', 'Information to extract: {{count}} tags', { count: tagCount })
            : tagsLabel}
          startIcon={<LocalOfferOutlinedIcon sx={{ fontSize: 15 }} />}
          endIcon={hasTagCount ? <Chip component="span" size="small" label={tagCount}
            sx={{ height: 21, minWidth: 22, fontSize: 11, bgcolor: 'action.hover', color: 'inherit',
              '& .MuiChip-label': { px: 0.6 } }} /> : null}
          sx={{ minHeight: 32, justifyContent: 'flex-start', textTransform: 'none', fontSize: 11, lineHeight: 1.3,
            '& .MuiButton-endIcon': { ml: 'auto' } }}>
          {tagsLabel}
        </Button>
      </Box>
    </Tooltip>
    <Tooltip title={t('dag.events_definition', 'An event is an item received from a source, such as a document, an email, or an Excel row.')}>
      <Box component="span" className="nodrag nopan" onClick={stopPropagation} onPointerDown={stopPropagation}
        sx={{ display: 'block', mt: 0.5 }}>
        <Button className="nodrag nopan" fullWidth size="small" variant="outlined"
          disabled={!openTimeline} onClick={activate(openTimeline)}
          onPointerDown={stopPropagation} onKeyDown={stopPropagation}
          startIcon={<ListAltOutlinedIcon sx={{ fontSize: 15 }} />}
          sx={{ minHeight: 32, textTransform: 'none', fontSize: 11, borderColor: 'divider' }}>
          {t('dag.view_events', 'View events')}
        </Button>
      </Box>
    </Tooltip>
    <DataUnfoldOperations reanalysis={reanalysis} actionControls={actionControls} t={t}
      menuButtonSx={{ right: 5, top: 5 }}
      statusSx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0 }} />
  </Paper>;
}
